import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FolderPlus } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useToast } from 'hooks/use-toast';
import { useCreatePromptFolder } from '../hooks/use-prompts';
import { DEFAULT_RESOURCE_VISIBILITY } from 'types/visibility';

type AddFolderForm = {
  name: string;
  visibility: 'private' | 'public';
};

type AddFolderModalProps = {
  trigger?: React.ReactNode;
  triggerClassName?: string;
  triggerLabel?: string;
  onCreated?: () => void;
};

export function AddFolderModal({
  trigger,
  triggerClassName,
  triggerLabel,
  onCreated,
}: AddFolderModalProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { mutateAsync: createFolder, isPending } = useCreatePromptFolder();
  const { t } = useTranslation();

  const addFolderSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t('FOLDER_NAME_REQUIRED') || 'Folder name is required'),
    visibility: z.enum(['private', 'public']),
  });

  const form = useForm<AddFolderForm>({
    resolver: zodResolver(addFolderSchema),
    defaultValues: { name: '', visibility: DEFAULT_RESOURCE_VISIBILITY },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      form.reset({ name: '', visibility: DEFAULT_RESOURCE_VISIBILITY });
    }
  };

  const onSubmit = async (values: AddFolderForm) => {
    try {
      await createFolder({ name: values.name, visibility: values.visibility });
      toast({
        variant: 'success',
        title: t('TOAST_FOLDER_CREATED_TITLE') || 'Folder created',
        description: t('TOAST_PROMPT_FOLDER_READY_DESCRIPTION') || 'Your prompt folder is ready.',
      });
      handleOpenChange(false);
      onCreated?.();
    } catch (error) {
      toast({
        title: t('TOAST_FAILED_TO_CREATE_FOLDER_TITLE') || 'Failed to create folder',
        description:
          error instanceof Error
            ? error.message
            : t('TOAST_PLEASE_TRY_AGAIN_DESCRIPTION') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (

          <ConfigurationButton
            className={triggerClassName}
            icon={<FolderPlus className="h-4 w-4" />}
          >
            {triggerLabel || t('ADD_FOLDER') || 'Add folder'}
          </ConfigurationButton>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('CREATE_FOLDER') || 'Create folder'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('FOLDER_NAME') || 'Folder name'}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('FOLDER_NAME') || 'Folder name'} {...field} />
                  </FormControl>
                  {/* <p className="text-xs text-muted-foreground">
                    {t('SHARED_WITH_ONLY_YOU') || 'Shared with: Only you'}
                  </p> */}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <VisibilityField
                      value={field.value}
                      onValueChange={field.onChange}
                      idPrefix="add-prompt-folder-visibility"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="shrink-0 gap-2 border-t border-border pt-4 sm:gap-3">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('CREATE_FOLDER') || 'Create folder'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
